import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { FamilyPaywallModal } from 'us-react-components/dist/FamilyPaywallModal/FamilyPaywallModal';
import {
  ScheduleEntry,
  Sitter,
  SitterService,
} from 'us-web-services';
import { AgendaList } from 'us-react-components/dist/Calendar/AgendaList';
import { BookingPreviewModal } from 'us-react-components/dist/Calendar/BookingPreviewModal';
import { CalendarToolbar } from 'us-react-components/dist/Calendar/CalendarToolbar';
import { MiniCalendar } from 'us-react-components/dist/Calendar/MiniCalendar';
import type { ViewMode } from 'us-react-components/dist/Calendar/types';
import { DayView } from 'us-react-components/dist/Calendar/views/DayView';
import { MonthView } from 'us-react-components/dist/Calendar/views/MonthView';
import { WeekView } from 'us-react-components/dist/Calendar/views/WeekView';
import {
  MONTHS,
  WEEKDAYS_LONG,
  addDays,
  addMonths,
  formatApiDateTime,
  startOfWeek,
} from 'us-react-components/dist/Calendar/utils';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { ModalName } from '../../modals/constants';
import { useModal } from '../../modals/modalContext';
import { ConfigService } from '../../services/ConfigService/ConfigService';
import { checkForPaywall } from '../../utils/PaywallUtil';
import { SitterProfileChrome } from '../SitterReviews/components/SitterProfileChrome';

const config = new ConfigService();

export const SitterCalendar = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const modal = useModal();
  const user = stores.user.user;

  const [sitter, setSitter] = useState<Sitter | null>(null);
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
  const [cursor, setCursor] = useState(() => new Date());
  const [view, setView] = useState<ViewMode>('week');
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [activeBooking, setActiveBooking] = useState<ScheduleEntry['booking'] | null>(null);

  const sitterId = pathId ? Number(pathId) : null;

  useEffect(() => {
    document.title = 'UrbanSitter - Calendar';
  }, []);

  useEffect(() => {
    if (!sitterId || !user?.id) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterId, {
          params: { userId: user.id, parentId: user.id },
        });
        setSitter((resp.data?.data ?? null) as Sitter | null);
      } catch (err) {
        console.warn('[SitterCalendar] sitter load failed', err);
      }
    };
    void load();
  }, [sitterId, user?.id]);

  useEffect(() => {
    if (!sitterId) return;
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const start = formatApiDateTime(new Date(year, month - 1, 1));
    const end = formatApiDateTime(new Date(year, month + 2, 1));
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.getSchedules({
          params: { sitterId, start, end },
        });
        setSchedule((resp.data?.data ?? []) as ScheduleEntry[]);
      } catch {
        setSchedule([]);
      }
    };
    void load();
  }, [sitterId, cursor]);

  const stepCursor = (delta: number): void => {
    setCursor((prev) => {
      if (view === 'month') return addMonths(prev, delta);
      if (view === 'week') return addDays(prev, 7 * delta);
      return addDays(prev, delta);
    });
  };
  const goToday = (): void => setCursor(new Date());

  const title = ((): string => {
    if (view === 'month') return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === 'week') {
      const s = startOfWeek(cursor);
      const e = addDays(s, 6);
      const sameMonth = s.getMonth() === e.getMonth();
      const left = `${MONTHS[s.getMonth()].slice(0, 3)} ${s.getDate()}`;
      const right = sameMonth
        ? `${e.getDate()}, ${e.getFullYear()}`
        : `${MONTHS[e.getMonth()].slice(0, 3)} ${e.getDate()}, ${e.getFullYear()}`;
      return `${left} – ${right}`;
    }
    return `${WEEKDAYS_LONG[cursor.getDay()]}, ${MONTHS[cursor.getMonth()]} ${cursor.getDate()}, ${cursor.getFullYear()}`;
  })();

  const openIdentity = (): void => modal?.setModalName(ModalName.IdentityVerification);

  const onBook = (): void => {
    if (!sitter?.id) return;
    stores.verification.checkVerification(() => {
      navigate(`/parent/bookings/select?sitterId=${sitter.id}`);
    }, openIdentity);
  };
  const onMessage = (): void => {
    if (!sitter?.id) return;
    stores.verification.checkVerification(() => {
      checkForPaywall({
        userId: user?.id,
        sitter,
        action: (): void => {
          navigate(`/parent/${user?.id}/messages/${sitter.id}`);
        },
        openPaywall: (): void => setPaywallOpen(true),
      });
    }, openIdentity);
  };
  const onInterview = (): void => {
    if (!sitter?.id) return;
    stores.verification.checkVerification(() => {
      navigate(`/parent/interviews/select?sitterId=${sitter.id}`);
    }, openIdentity);
  };

  const openBookingPreview = (entry: ScheduleEntry): void => {
    if (!entry.booking) return;
    setActiveBooking(entry.booking);
    setBookingOpen(true);
  };

  if (!sitter || !sitterId) {
    return (
      <div className='container py-8'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  const reviewCount = sitter.reviewCount ?? 0;
  const repeatFamilies = sitter.repeatFamilies ?? 0;
  const responseTime = sitter.responseTime ?? 0;

  return (
    <div className='container py-6 max-w-7xl mx-auto'>
      <SitterProfileChrome
        sitter={sitter}
        activeTab='calendar'
        viewerUserId={user?.id}
      />

      <div className='grid grid-cols-1 md:grid-cols-12 gap-4'>
        <main className='md:col-span-9'>
          <div className='hidden md:block'>
            <CalendarToolbar
              title={title}
              view={view}
              onView={setView}
              onPrev={(): void => stepCursor(-1)}
              onToday={goToday}
              onNext={(): void => stepCursor(1)}
              namespace={namespaces.family}
            />

            {view === 'week' && (
              <WeekView
                cursor={cursor}
                schedule={schedule}
                canEdit={false}
                onCreate={(): void => undefined}
                onEditAvailability={(): void => undefined}
                onOpenBooking={openBookingPreview}
                namespace={namespaces.family}
                showBookingDetails={false}
              />
            )}
            {view === 'day' && (
              <DayView
                cursor={cursor}
                schedule={schedule}
                canEdit={false}
                onCreate={(): void => undefined}
                onEditAvailability={(): void => undefined}
                onOpenBooking={openBookingPreview}
                namespace={namespaces.family}
                showBookingDetails={false}
              />
            )}
            {view === 'month' && (
              <MonthView
                cursor={cursor}
                schedule={schedule}
                canEdit={false}
                onCreateOnDay={(): void => undefined}
                onOpenBooking={openBookingPreview}
                onEditAvailability={(): void => undefined}
                onJumpToDay={(d): void => {
                  setCursor(d);
                  setView('day');
                }}
                namespace={namespaces.family}
                showBookingDetails={false}
              />
            )}
          </div>

          <div className='md:hidden space-y-3'>
            <MiniCalendar
              cursor={cursor}
              onSelectDate={(d): void => setCursor(d)}
              schedule={schedule}
              namespace={namespaces.family}
            />
            <AgendaList
              schedule={schedule}
              onSelectDate={(d): void => setCursor(d)}
              namespace={namespaces.family}
              emptyMessage={t('pages.calendar.noUpcomingFor', { name: sitter.firstName ?? '' })}
            />
          </div>
        </main>

        <aside className='md:col-span-3 space-y-3'>
          <div className='us-card rounded p-4 text-center bg-white'>
            {sitter.rating != null && (
              <div
                className='flex justify-center gap-0.5 mb-1'
                role='img'
                aria-label={t('pages.calendar.ratingAria', { rating: sitter.rating })}
              >
                {[0, 1, 2, 3, 4].map((i) => (
                  <FontAwesomeIcon
                    key={i}
                    icon={faStar}
                    aria-hidden='true'
                    className={i < (sitter.rating ?? 0) ? 'text-honey' : 'text-neutral-300'}
                  />
                ))}
              </div>
            )}
            <h4 className='text-sm font-semibold mb-2'>
              {reviewCount > 0 && t('pages.calendar.reviewsCount', { count: reviewCount })}
              {reviewCount > 0 && repeatFamilies > 0 && <span>{' | '}</span>}
              {repeatFamilies > 0 && t('pages.calendar.repeatFamiliesCount', { count: repeatFamilies })}
              {responseTime > 0 && (
                <div>
                  {t('pages.calendar.respondsIn', { count: responseTime })}
                </div>
              )}
            </h4>
          </div>

          {user?.parent && user.id !== sitter.id && (
            <div className='space-y-2'>
              <Button
                id={`booking-${sitter.id}`}
                buttonType={ButtonType.primary}
                className='w-full'
                onClick={onBook}
              >
                {t('pages.calendar.requestABooking')}
              </Button>
              <Button
                id={`message-${sitter.id}`}
                buttonType={ButtonType.secondary}
                className='w-full'
                onClick={onMessage}
              >
                {t('pages.calendar.message')}
              </Button>
              <Button
                id={`interview-${sitter.id}`}
                buttonType={ButtonType.secondary}
                className='w-full'
                onClick={onInterview}
              >
                {t('pages.calendar.interview')}
              </Button>
            </div>
          )}
        </aside>
      </div>

      <BookingPreviewModal
        isOpen={bookingOpen}
        onClose={(): void => setBookingOpen(false)}
        booking={activeBooking}
        namespace={namespaces.family}
        showContactDetails={false}
      />

      {user?.id && (
        <FamilyPaywallModal
          showModal={paywallOpen}
          onClose={(): void => setPaywallOpen(false)}
          onSuccess={(): void => {
            setPaywallOpen(false);
            navigate(`/parent/${user.id}/messages/${sitter.id}`);
          }}
          sitter={sitter as Sitter}
          userId={user.id}
          stripeKey={config.STRIPE_KEY}
          recaptchaKey={config.RECAPTCHA_KEY}
        />
      )}
    </div>
  );
});
